import { Switch } from "@ark-ui/react";
import {
  ALL_FIM_ITEMS,
  DELETION_FLAG,
  type DeletionFlag,
  ERROR_CODE,
  type Facility,
  type FacilityKey,
  UniversalError,
  toPathSafeString,
} from "@packages/core";
import { createFileRoute } from "@tanstack/react-router";
import Encoding from "encoding-japanese";
import { zip } from "fflate";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Subject, concatMap, mergeMap } from "rxjs";
import { Temporal } from "temporal-polyfill";
import { create } from "zustand";
import {
  ButtonStyle,
  FacilitySelector,
  FormField,
  FormFieldInput,
  FormFieldInputStyle,
  PageName,
  SwitchStyle,
  TypographyStyle,
  showError,
} from "#components";
import {
  findFacility,
  findFallRiskEntries,
  findFallScoreEntries,
  findIndependenceLevelEntries,
  findResidents,
  findStatuses,
  listFalls,
  listMeasurements,
  saveAsFile,
  useFacilityInfiniteQuery,
} from "#services";
import { css, cx } from "#styled-system/css";

export const Route = createFileRoute("/_authenticated/management/resident-data-exporter")({
  component: Component,
});

function Component() {
  const { t } = useTranslation();

  const logs = useResidentDataExporterStore(function (state) {
    return state.logs;
  });
  const setDependencies = useResidentDataExporterStore(function (state) {
    return state.setDependencies;
  });
  const start = useResidentDataExporterStore(function (state) {
    return state.start;
  });
  const exportFallRiskEntries = useResidentDataExporterStore(function (state) {
    return state.exportFallRiskEntries;
  });
  const setExportFallRiskEntries = useResidentDataExporterStore(function (state) {
    return state.setExportFallRiskEntries;
  });
  const exportFallScoreEntries = useResidentDataExporterStore(function (state) {
    return state.exportFallScoreEntries;
  });
  const setExportFallScoreEntries = useResidentDataExporterStore(function (state) {
    return state.setExportFallScoreEntries;
  });
  const exportFalls = useResidentDataExporterStore(function (state) {
    return state.exportFalls;
  });
  const setExportFalls = useResidentDataExporterStore(function (state) {
    return state.setExportFalls;
  });
  const exportIndependenceLevelEntries = useResidentDataExporterStore(function (state) {
    return state.exportIndependenceLevelEntries;
  });
  const setExportIndependenceLevelEntries = useResidentDataExporterStore(function (state) {
    return state.setExportIndependenceLevelEntries;
  });
  const exportMeasurements = useResidentDataExporterStore(function (state) {
    return state.exportMeasurements;
  });
  const setExportMeasurements = useResidentDataExporterStore(function (state) {
    return state.setExportMeasurements;
  });
  const exportStatuses = useResidentDataExporterStore(function (state) {
    return state.exportStatuses;
  });
  const setExportStatuses = useResidentDataExporterStore(function (state) {
    return state.setExportStatuses;
  });

  const [facility, setFacility] = useState<Facility | null>(null);
  const [period, setPeriod] = useState(function () {
    const now = Temporal.Now.plainDateTimeISO();
    return [
      now
        .subtract({ months: 1, hours: 1 })
        .with({ minute: 0, second: 0, millisecond: 0, microsecond: 0, nanosecond: 0 }),
      now
        .subtract({ hours: 1 })
        .with({ minute: 0, second: 0, millisecond: 0, microsecond: 0, nanosecond: 0 }),
    ] as const;
  });
  const [targetAvailabilities, setTargetAvailabilities] = useState(function () {
    return new Set<DeletionFlag>([DELETION_FLAG.ARCHIVED, DELETION_FLAG.DEFAULT]);
  });

  const facilityInfiniteQuery = useFacilityInfiniteQuery({ limit: 25 });
  const facilities = useMemo(
    function () {
      return (
        facilityInfiniteQuery.data?.pages.flatMap(function (page) {
          return page.facilities;
        }) ?? []
      );
    },
    [facilityInfiniteQuery.data],
  );

  useEffect(
    function () {
      setDependencies({ translate: t });
    },
    [setDependencies, t],
  );

  return (
    <main>
      <PageName>{t("%ResidentDataConfirmation.0%")}</PageName>

      <h1
        className={TypographyStyle({
          ellipsis: true,
          fontSize: "24px",
          fontWeight: 700,
          lineHeight: 1,
        })}
      >
        {t("%ResidentDataConfirmation.0%")}
      </h1>

      <div
        className={css({
          backgroundColor: "#FAFAFA",
          borderRadius: "12px",
          marginTop: "16px",
          padding: "16px 24px",
        })}
      >
        <h2
          className={TypographyStyle({
            ellipsis: true,
            fontSize: "18px",
            fontWeight: 600,
            lineHeight: 1,
          })}
        >
          {t("%SelectFacility.0%")}
        </h2>

        <div
          className={css({
            display: "flex",
            gap: "8px",
            marginTop: "16px",
          })}
        >
          <FormField label={t("%Facility.0%")}>
            <FacilitySelector
              className={css({ maxWidth: "320px", width: "stretch" })}
              load={async function () {
                await facilityInfiniteQuery.fetchNextPage();
              }}
              loaded={!facilityInfiniteQuery.hasNextPage}
              loading={facilityInfiniteQuery.isFetching}
              onChange={function (facility) {
                setFacility(facility);
              }}
              options={facilities}
              title={t("%Facility.0%")}
              value={facility}
            />
          </FormField>
        </div>
      </div>

      <div
        className={css({
          backgroundColor: "#FAFAFA",
          borderRadius: "12px",
          marginTop: "16px",
          padding: "16px 24px",
        })}
      >
        <h2
          className={TypographyStyle({
            ellipsis: true,
            fontSize: "18px",
            fontWeight: 600,
            lineHeight: 1,
          })}
        >
          {t("%SelectResident.0%")}
        </h2>

        <div className={css({ marginTop: "16px" })}>
          <Switch.Root
            checked={targetAvailabilities.has(DELETION_FLAG.DEFAULT)}
            className={css({
              alignItems: "center",
              cursor: "pointer",
              display: "inline-flex",
              gap: "8px",
            })}
            onCheckedChange={function (event) {
              setTargetAvailabilities(function (previous) {
                if (event.checked && !previous.has(DELETION_FLAG.DEFAULT)) {
                  const next = new Set(previous);
                  next.add(DELETION_FLAG.DEFAULT);
                  return next;
                } else if (!event.checked && previous.has(DELETION_FLAG.DEFAULT)) {
                  const next = new Set(previous);
                  next.delete(DELETION_FLAG.DEFAULT);
                  return next;
                } else {
                  return previous;
                }
              });
            }}
          >
            <Switch.Control>
              <Switch.Thumb className={SwitchStyle({ display: "block" })} />
            </Switch.Control>
            <Switch.Label className={TypographyStyle({ fontSize: "14px", lineHeight: 1 })}>
              {t("%IncludeResidents.0%")}
            </Switch.Label>
            <Switch.HiddenInput />
          </Switch.Root>
        </div>

        <div className={css({ marginTop: "16px" })}>
          <Switch.Root
            checked={targetAvailabilities.has(DELETION_FLAG.ARCHIVED)}
            className={css({
              alignItems: "center",
              cursor: "pointer",
              display: "inline-flex",
              gap: "8px",
            })}
            onCheckedChange={function (event) {
              setTargetAvailabilities(function (previous) {
                if (event.checked && !previous.has(DELETION_FLAG.ARCHIVED)) {
                  const next = new Set(previous);
                  next.add(DELETION_FLAG.ARCHIVED);
                  return next;
                } else if (!event.checked && previous.has(DELETION_FLAG.ARCHIVED)) {
                  const next = new Set(previous);
                  next.delete(DELETION_FLAG.ARCHIVED);
                  return next;
                } else {
                  return previous;
                }
              });
            }}
          >
            <Switch.Control>
              <Switch.Thumb className={SwitchStyle({ display: "block" })} />
            </Switch.Control>
            <Switch.Label className={TypographyStyle({ fontSize: "14px", lineHeight: 1 })}>
              {t("%IncludeResidents.1%")}
            </Switch.Label>
            <Switch.HiddenInput />
          </Switch.Root>
        </div>
      </div>

      <div
        className={css({
          backgroundColor: "#FAFAFA",
          borderRadius: "12px",
          marginTop: "16px",
          padding: "16px 24px",
        })}
      >
        <h2
          className={TypographyStyle({
            ellipsis: true,
            fontSize: "18px",
            fontWeight: 600,
            lineHeight: 1,
          })}
        >
          {t("%SelectTarget.0%")}
        </h2>

        <div className={css({ display: "flex", flexWrap: "wrap", gap: "32px", marginTop: "16px" })}>
          {(
            [
              [exportFallRiskEntries, setExportFallRiskEntries, t("%FallRiskEntry.0%")],
              [exportFallScoreEntries, setExportFallScoreEntries, t("%FallScoreEntry.0%")],
              [exportFalls, setExportFalls, t("%Fall.1%")],
              [
                exportIndependenceLevelEntries,
                setExportIndependenceLevelEntries,
                t("%IndependenceLevelEntry.0%"),
              ],
              [exportMeasurements, setExportMeasurements, t("%Measurement.1%")],
              [exportStatuses, setExportStatuses, t("%Status.1%")],
            ] as const
          ).map(function ([value, setValue, label], index) {
            return (
              <Switch.Root
                checked={value}
                className={css({
                  alignItems: "center",
                  cursor: "pointer",
                  display: "inline-flex",
                  gap: "8px",
                })}
                key={index}
                onCheckedChange={function (event) {
                  setValue(event.checked);
                }}
              >
                <Switch.Control>
                  <Switch.Thumb className={SwitchStyle({ display: "block" })} />
                </Switch.Control>
                <Switch.Label className={TypographyStyle({ fontSize: "14px", lineHeight: 1 })}>
                  {label}
                </Switch.Label>
                <Switch.HiddenInput />
              </Switch.Root>
            );
          })}
        </div>
      </div>

      <div
        className={css({
          backgroundColor: "#FAFAFA",
          borderRadius: "12px",
          marginTop: "16px",
          padding: "16px 24px",
        })}
      >
        <h2
          className={TypographyStyle({
            ellipsis: true,
            fontSize: "18px",
            fontWeight: 600,
            lineHeight: 1,
          })}
        >
          {t("%SelectPeriod.0%")}
        </h2>

        <div
          className={css({
            display: "flex",
            gap: "8px",
            marginTop: "16px",
          })}
        >
          <FormField description={t("%Period.1%")} label={t("%Period.0%")}>
            <div className={css({ alignItems: "center", display: "flex", gap: "8px" })}>
              <FormFieldInput
                className={css(
                  TypographyStyle.raw({ fontSize: "14px", lineHeight: 1 }),
                  FormFieldInputStyle.raw({ backgroundColor: "white", flexGrow: 1 }),
                )}
                onChange={function (event) {
                  console.info("[onChange]", event);
                  setPeriod(function (period) {
                    return [Temporal.PlainDateTime.from(event.target.value), period[1]];
                  });
                }}
                type="datetime-local"
                value={period[0].toString({ smallestUnit: "minute" })}
              />
              <span className={TypographyStyle({ fontSize: "14px", lineHeight: 1 })}>–</span>
              <FormFieldInput
                className={css(
                  TypographyStyle.raw({ fontSize: "14px", lineHeight: 1 }),
                  FormFieldInputStyle.raw({ backgroundColor: "white", flexGrow: 1 }),
                )}
                onChange={function (event) {
                  console.info("[onChange]", event);
                  setPeriod(function (period) {
                    return [period[0], Temporal.PlainDateTime.from(event.target.value)];
                  });
                }}
                type="datetime-local"
                value={period[1].toString({ smallestUnit: "minute" })}
              />
            </div>
          </FormField>
        </div>
      </div>

      <button
        className={css(
          ButtonStyle.raw({
            appearance: "contained",
            borderRadius: "4px",
            color: "primary",
            flexGrow: 1,
            padding: "10px 16px",
          }),
          { marginTop: "16px" },
        )}
        disabled={
          !facility ||
          targetAvailabilities.size === 0 ||
          Temporal.PlainDateTime.compare(period[0], period[1]) >= 0
        }
        onClick={function (event) {
          event.preventDefault();

          if (
            !facility ||
            targetAvailabilities.size === 0 ||
            Temporal.PlainDateTime.compare(period[0], period[1]) >= 0
          ) {
            return;
          }

          start({ facilityKey: facility.facilityKey, period, targetAvailabilities });
        }}
      >
        {t("%Export.0%")}
      </button>

      <div
        className={cx(
          css({
            backgroundColor: "#FAFAFA",
            borderRadius: "12px",
            marginTop: "16px",
            padding: "16px 24px",
          }),
          logs.size === 0 && css({ display: "none" }),
        )}
      >
        <h2
          className={TypographyStyle({
            ellipsis: true,
            fontSize: "18px",
            fontWeight: 600,
            lineHeight: 1,
          })}
        >
          {t("%Log.0%")}
        </h2>

        {Array.from(logs.values()).map(function (logs, index) {
          return (
            <AutoScrollTextArea
              className={css({
                backgroundColor: "white",
                borderRadius: "4px",
                borderWidth: "1px",
                borderStyle: "solid",
                borderColor: "border",
                fontSize: "12px",
                marginTop: "8px",
                padding: "8px",
                width: "stretch",
              })}
              key={index}
              readOnly
              rows={8}
              value={logs.join("\n")}
            />
          );
        })}
      </div>
    </main>
  );
}

type AutoScrollTextAreaProperties = React.ComponentProps<"textarea">;
function AutoScrollTextArea({ value, ...properties }: AutoScrollTextAreaProperties) {
  const reference = useRef<HTMLTextAreaElement>(null);

  useEffect(
    function () {
      const element = reference.current;
      if (element) {
        element.scrollTop = element.scrollHeight;
      }
    },
    [value],
  );

  return <textarea {...properties} ref={reference} value={value} />;
}

type Item = {
  id: number;
  facilityKey: FacilityKey;
  period: readonly [Temporal.PlainDateTime, Temporal.PlainDateTime];
  targetAvailabilities: Set<DeletionFlag>;
};

type ResidentDataExporterStore = {
  start: (item: Omit<Item, "id">) => void;
  logs: Map<number, Array<string>>;
  subject: Subject<Item>;
  dependencies?: Partial<{ translate: { (key: string, options?: any): string } }>;
  setDependencies: {
    (
      dependencies: Partial<{
        translate: { (key: string, options?: any): string };
      }>,
    ): void;
  };
  exportFallRiskEntries: boolean;
  setExportFallRiskEntries: { (value: boolean): void };
  exportFallScoreEntries: boolean;
  setExportFallScoreEntries: { (value: boolean): void };
  exportFalls: boolean;
  setExportFalls: { (value: boolean): void };
  exportIndependenceLevelEntries: boolean;
  setExportIndependenceLevelEntries: { (value: boolean): void };
  exportMeasurements: boolean;
  setExportMeasurements: { (value: boolean): void };
  exportStatuses: boolean;
  setExportStatuses: { (value: boolean): void };
};

const useResidentDataExporterStore = create<ResidentDataExporterStore>(function (set, get) {
  let _id = 0;
  const subject = new Subject<Item>();

  function start(item: Omit<Item, "id">) {
    const id = ++_id;
    subject.next({ ...item, id });
  }

  function toDateTimeString(dateTime: Temporal.PlainDateTime) {
    return (
      dateTime.year.toString().padStart(4, "0") +
      dateTime.month.toString().padStart(2, "0") +
      dateTime.day.toString().padStart(2, "0") +
      dateTime.hour.toString().padStart(2, "0") +
      dateTime.minute.toString().padStart(2, "0") +
      dateTime.second.toString().padStart(2, "0")
    );
  }

  function toBytes(string: string) {
    return new Uint8Array(
      Encoding.convert(Encoding.stringToCode(string), { from: "UNICODE", to: "SJIS" }),
    );
  }

  subject
    .pipe(
      mergeMap(async function ({ facilityKey, period, targetAvailabilities, id }) {
        function log(message: string) {
          set(function ({ logs }) {
            const next = new Map(logs);
            const facilityLogs = next.get(id) ?? [];
            const now = Temporal.Now.plainDateTimeISO("Asia/Tokyo");
            facilityLogs.push(
              "[" +
                now.hour.toString().padStart(2, "0") +
                ":" +
                now.minute.toString().padStart(2, "0") +
                "] " +
                message,
            );
            next.set(id, facilityLogs);
            return { logs: next };
          });
        }

        // 依存性を参照する。
        const {
          dependencies,
          exportFallRiskEntries,
          exportFallScoreEntries,
          exportFalls,
          exportIndependenceLevelEntries,
          exportMeasurements,
          exportStatuses,
        } = get();
        if (typeof dependencies?.translate !== "function") {
          log("依存性の初期化に失敗しました。");
          return new UniversalError(ERROR_CODE.UNINITIALIZED);
        }
        const { translate } = dependencies;

        log(translate("%ResidentDataExporter.1%", { identifier: id }));

        return {
          exportFallRiskEntries,
          exportFallScoreEntries,
          exportFalls,
          exportIndependenceLevelEntries,
          exportMeasurements,
          exportStatuses,
          facilityKey,
          log,
          period,
          targetAvailabilities,
          translate,
        };
      }),
      concatMap(async function (parameters) {
        // エラーを伝播する。
        if (parameters instanceof Error) {
          return parameters;
        }

        const {
          exportFallRiskEntries,
          exportFallScoreEntries,
          exportFalls,
          exportIndependenceLevelEntries,
          exportMeasurements,
          exportStatuses,
          facilityKey,
          log,
          period,
          targetAvailabilities,
          translate,
        } = parameters;

        log(translate("%ResidentDataExporter.2%"));

        const fileContents = new Map<string, Uint8Array>();

        let facility;
        try {
          facility = await findFacility(facilityKey);
        } catch (error) {
          console.error(error);
          log(translate("%ResidentDataExporter.3%"));
          return new UniversalError(ERROR_CODE.ABORTED);
        }

        if (!facility) {
          log(translate("%ResidentDataExporter.4%"));
          return new UniversalError(ERROR_CODE.ABORTED);
        }

        const facilityDirectoryName =
          facility.facilityKey +
          " " +
          toPathSafeString(facility.name) +
          " (" +
          toDateTimeString(Temporal.Now.plainDateTimeISO("Asia/Tokyo")) +
          ")";

        log(translate("%ResidentDataExporter.5%", { $: { facility } }));

        log("入居者を取得します。");

        const residents = [];
        for (const deleted of targetAvailabilities) {
          let nextPageToken;
          do {
            let response;
            try {
              response = await findResidents({
                index: "facilityKey-deleted-gsi",
                condition: [
                  ["facilityKey", "=", facility.facilityKey],
                  ["deleted", "=", deleted],
                ],
              });
              nextPageToken = response.nextPageToken;
            } catch (error) {
              console.error(error);
              log("入居者の取得に失敗しました。");
              return new UniversalError(ERROR_CODE.ABORTED);
            }

            if (response.residents.length === 0) {
              continue;
            }

            log(
              response.residents
                .map(function (resident) {
                  return resident.name + " (" + resident.userKey + ")";
                })
                .join(", ") + "を取得しました。",
            );

            residents.push(...response.residents);
          } while (nextPageToken);
        }

        if (residents.length === 0) {
          log("対象となる入居者が見つかりませんでした。");
          return;
        }

        log(residents.length + "名の入居者をダウンロードしました。");

        fileContents.set(
          [facilityDirectoryName, facility.facilityKey + "_residents.csv"].join("/"),
          toBytes(
            [
              [
                translate("%FacilityKey.0%") + " (facilityKey)",
                translate("%UserKey.0%") + " (userKey)",
                translate("%Name.0%") + " (name)",
                translate("%Sex.0%") + " (sex)",
                translate("%Height.1%") + " (height)",
                translate("%Weight.1%") + " (weight)",
                translate("%CreationDateTime.0%") + " (createdAt)",
                translate("%UpdateDateTime.0%") + " (updatedAt)",
                translate("%Migration.0%") + " (migrated)",
                translate("%MigrationDateTime.0%") + " (migratedAt)",
                translate("%MigrationSource.0%") + " (migratedFrom)",
              ].join(","),
              ...residents.map(function (resident) {
                return [
                  resident.facilityKey,
                  resident.userKey,
                  resident.name.replace(",", "，"),
                  resident.sex,
                  resident.height,
                  resident.weight,
                  resident.createdAt.toString(),
                  resident.updatedAt.toString(),
                  resident.migrated ?? "",
                  resident.migratedAt?.toString() ?? "",
                  resident.migratedFrom ?? "",
                ].join(",");
              }),
            ].join("\n"),
          ),
        );

        for (const resident of residents) {
          const residentDirectoryName =
            resident.userKey +
            " " +
            toPathSafeString(resident.name) +
            " (" +
            toDateTimeString(Temporal.Now.plainDateTimeISO("Asia/Tokyo")) +
            ")";

          log(resident.name + " (" + resident.userKey + ")の処理を開始します。");

          if (exportFallRiskEntries) {
            log(
              resident.name +
                " (" +
                resident.userKey +
                ")の転倒リスクエントリをダウンロードします。",
            );

            const fallRiskEntries = [];

            {
              let nextPageToken;
              do {
                let response;
                try {
                  response = await findFallRiskEntries({
                    condition: [
                      ["userKey", "=", resident.userKey],
                      ["assessedAt", "BETWEEN", period[0], "AND", period[1]],
                    ],
                    nextPageToken,
                    limit: 100,
                  });
                  nextPageToken = response.nextPageToken;
                } catch (error) {
                  console.error(error);
                  log(
                    resident.name +
                      " (" +
                      resident.userKey +
                      ")の転倒リスクエントリの取得に失敗しました。",
                  );
                  return;
                }

                if (response.fallRiskEntries.length === 0) {
                  continue;
                }

                log(
                  resident.name +
                    " (" +
                    resident.userKey +
                    ")の転倒リスクエントリ" +
                    response.fallRiskEntries.length +
                    "件をダウンロードしました。",
                );

                fallRiskEntries.push(...response.fallRiskEntries);
              } while (nextPageToken);
            }

            if (fallRiskEntries.length > 0) {
              log(
                resident.name +
                  " (" +
                  resident.userKey +
                  ")の転倒リスクエントリ" +
                  fallRiskEntries.length +
                  "件のダウンロードが完了しました。ファイルを作成します。",
              );

              fileContents.set(
                [
                  facilityDirectoryName,
                  residentDirectoryName,
                  resident.userKey + "_fall_risk_entries.csv",
                ].join("/"),
                toBytes(
                  [
                    [
                      translate("%UserKey.0%") + " (userKey)",
                      translate("%AssessmentDateTime.0%") + " (assessedAt)",
                      translate("%FacilityKey.0%") + " (facilityKey)",
                      translate("%GroupKey.0%") + " (groupKey)",
                      translate("%AssessmentType.0%") + " (type)",
                      translate("%FallRisk.0%") + " (fallRisk)",
                      translate("%CreationDateTime.0%") + " (createdAt)",
                      translate("%UpdateDateTime.0%") + " (updatedAt)",
                      translate("%Migration.0%") + " (migrated)",
                      translate("%MigrationDateTime.0%") + " (migratedAt)",
                      translate("%MigrationSource.0%") + " (migratedFrom)",
                    ].join(","),
                    ...fallRiskEntries.map(function (fallRiskEntry) {
                      return [
                        fallRiskEntry.userKey,
                        fallRiskEntry.assessedAt.toString(),
                        fallRiskEntry.facilityKey,
                        fallRiskEntry.groupKey,
                        fallRiskEntry.type,
                        fallRiskEntry.fallRisk,
                        fallRiskEntry.createdAt.toString(),
                        fallRiskEntry.updatedAt.toString(),
                        fallRiskEntry.migrated ?? "",
                        fallRiskEntry.migratedAt?.toString() ?? "",
                        fallRiskEntry.migratedFrom ?? "",
                      ].join(",");
                    }),
                  ].join("\n"),
                ),
              );
            } else {
              log(
                resident.name + " (" + resident.userKey + ")の評価データは見つかりませんでした。",
              );
            }
          }

          if (exportFallScoreEntries) {
            log(
              resident.name +
                " (" +
                resident.userKey +
                ")の転倒スコアエントリをダウンロードします。",
            );

            const fallScoreEntries = [];

            {
              let nextPageToken;
              do {
                let response;
                try {
                  response = await findFallScoreEntries({
                    condition: [
                      ["userKey", "=", resident.userKey],
                      ["assessedAt", "BETWEEN", period[0], "AND", period[1]],
                    ],
                    nextPageToken,
                    limit: 100,
                  });
                  nextPageToken = response.nextPageToken;
                } catch (error) {
                  console.error(error);
                  log(
                    resident.name +
                      " (" +
                      resident.userKey +
                      ")の転倒スコアエントリの取得に失敗しました。",
                  );
                  return;
                }

                if (response.fallScoreEntries.length === 0) {
                  continue;
                }

                log(
                  resident.name +
                    " (" +
                    resident.userKey +
                    ")の転倒スコアエントリ" +
                    response.fallScoreEntries.length +
                    "件をダウンロードしました。",
                );

                fallScoreEntries.push(...response.fallScoreEntries);
              } while (nextPageToken);
            }

            if (fallScoreEntries.length > 0) {
              log(
                resident.name +
                  " (" +
                  resident.userKey +
                  ")の転倒スコアエントリ" +
                  fallScoreEntries.length +
                  "件のダウンロードが完了しました。ファイルを作成します。",
              );

              fileContents.set(
                [
                  facilityDirectoryName,
                  residentDirectoryName,
                  resident.userKey + "_fall_score_entries.csv",
                ].join("/"),
                toBytes(
                  [
                    [
                      translate("%UserKey.0%") + " (userKey)",
                      translate("%AssessmentDateTime.0%") + " (assessedAt)",
                      translate("%AssessmentType.0%") + " (type)",
                      translate("%FallScore.0%") + " (fallScore)",
                      translate("%CreationDateTime.0%") + " (createdAt)",
                      translate("%UpdateDateTime.0%") + " (updatedAt)",
                      translate("%Migration.0%") + " (migrated)",
                      translate("%MigrationDateTime.0%") + " (migratedAt)",
                      translate("%MigrationSource.0%") + " (migratedFrom)",
                    ].join(","),
                    ...fallScoreEntries.map(function (fallScoreEntry) {
                      return [
                        fallScoreEntry.userKey,
                        fallScoreEntry.assessedAt.toString(),
                        fallScoreEntry.type,
                        fallScoreEntry.fallScore,
                        fallScoreEntry.createdAt.toString(),
                        fallScoreEntry.updatedAt.toString(),
                        fallScoreEntry.migrated ?? "",
                        fallScoreEntry.migratedAt?.toString() ?? "",
                        fallScoreEntry.migratedFrom ?? "",
                      ].join(",");
                    }),
                  ].join("\n"),
                ),
              );
            } else {
              log(
                resident.name + " (" + resident.userKey + ")の評価データは見つかりませんでした。",
              );
            }
          }

          if (exportFalls) {
            log(resident.name + " (" + resident.userKey + ")の転倒データをダウンロードします。");

            const falls = [];

            {
              let nextPageToken;
              do {
                let response;
                try {
                  response = await listFalls(
                    {
                      userKey: resident.userKey,
                      deleted: DELETION_FLAG.DEFAULT,
                      fellAt: ["-", ...period],
                    },
                    { nextPageToken, pageSize: 100 },
                  );
                  nextPageToken = response.nextPageToken;
                } catch (error) {
                  console.error(error);
                  log(
                    resident.name + " (" + resident.userKey + ")の転倒データの取得に失敗しました。",
                  );
                  return;
                }

                if (response.falls.length === 0) {
                  continue;
                }

                log(
                  resident.name +
                    " (" +
                    resident.userKey +
                    ")の転倒データ" +
                    response.falls.length +
                    "件をダウンロードしました。",
                );

                falls.push(...response.falls);
              } while (nextPageToken);
            }

            if (falls.length > 0) {
              log(
                resident.name +
                  " (" +
                  resident.userKey +
                  ")の転倒データ" +
                  falls.length +
                  "件のダウンロードが完了しました。ファイルを作成します。",
              );

              fileContents.set(
                [
                  facilityDirectoryName,
                  residentDirectoryName,
                  resident.userKey + "_falls.csv",
                ].join("/"),
                toBytes(
                  [
                    [
                      translate("%UserKey.0%") + " (userKey)",
                      translate("%FallDateTime.0%") + " (fellAt)",
                      translate("%Registerer.0%") + " (registeredBy)",
                      translate("%CreationDateTime.0%") + " (createdAt)",
                      translate("%UpdateDateTime.0%") + " (updatedAt)",
                      translate("%Migration.0%") + " (migrated)",
                      translate("%MigrationDateTime.0%") + " (migratedAt)",
                      translate("%MigrationSource.0%") + " (migratedFrom)",
                    ].join(","),
                    ...falls.map(function (fall) {
                      return [
                        fall.userKey,
                        fall.fellAt.toString(),
                        fall.registeredBy ?? "",
                        fall.createdAt.toString(),
                        fall.updatedAt.toString(),
                        fall.migrated ?? "",
                        fall.migratedAt?.toString() ?? "",
                        fall.migratedFrom ?? "",
                      ].join(",");
                    }),
                  ].join("\n"),
                ),
              );
            } else {
              log(
                resident.name + " (" + resident.userKey + ")の転倒データは見つかりませんでした。",
              );
            }
          }

          if (exportIndependenceLevelEntries) {
            log(
              resident.name + " (" + resident.userKey + ")の自立度エントリをダウンロードします。",
            );

            const independenceLevelEntries = [];

            {
              let nextPageToken;
              do {
                let response;
                try {
                  response = await findIndependenceLevelEntries({
                    condition: [
                      ["userKey", "=", resident.userKey],
                      ["assessedAt", "BETWEEN", period[0], "AND", period[1]],
                    ],
                    nextPageToken,
                    limit: 100,
                  });
                  nextPageToken = response.nextPageToken;
                } catch (error) {
                  console.error(error);
                  log(
                    resident.name +
                      " (" +
                      resident.userKey +
                      ")の自立度エントリの取得に失敗しました。",
                  );
                  return;
                }

                if (response.independenceLevelEntries.length === 0) {
                  continue;
                }

                log(
                  resident.name +
                    " (" +
                    resident.userKey +
                    ")の自立度エントリ" +
                    response.independenceLevelEntries.length +
                    "件をダウンロードしました。",
                );

                independenceLevelEntries.push(...response.independenceLevelEntries);
              } while (nextPageToken);
            }

            if (independenceLevelEntries.length > 0) {
              log(
                resident.name +
                  " (" +
                  resident.userKey +
                  ")の自立度エントリ" +
                  independenceLevelEntries.length +
                  "件のダウンロードが完了しました。ファイルを作成します。",
              );

              fileContents.set(
                [
                  facilityDirectoryName,
                  residentDirectoryName,
                  resident.userKey + "_independence_level_entries.csv",
                ].join("/"),
                toBytes(
                  [
                    [
                      translate("%UserKey.0%") + " (userKey)",
                      translate("%AssessmentDateTime.0%") + " (assessedAt)",
                      translate("%AssessmentType.0%") + " (type)",
                      ...ALL_FIM_ITEMS.map(function (fimItem) {
                        return (
                          translate("%IndependenceLevel.0%") +
                          String.fromCharCode(65 + fimItem) +
                          " - " +
                          translate("%FimItem." + fimItem + ".0%") +
                          " (independenceLevels[" +
                          fimItem +
                          "])"
                        );
                      }),
                      translate("%CreationDateTime.0%") + " (createdAt)",
                      translate("%UpdateDateTime.0%") + " (updatedAt)",
                      translate("%Migration.0%") + " (migrated)",
                      translate("%MigrationDateTime.0%") + " (migratedAt)",
                      translate("%MigrationSource.0%") + " (migratedFrom)",
                    ].join(","),
                    ...independenceLevelEntries.map(function (independenceLevelEntry) {
                      return [
                        independenceLevelEntry.userKey,
                        independenceLevelEntry.assessedAt.toString(),
                        independenceLevelEntry.type,
                        ...independenceLevelEntry.independenceLevels,
                        independenceLevelEntry.createdAt.toString(),
                        independenceLevelEntry.updatedAt.toString(),
                        independenceLevelEntry.migrated ?? "",
                        independenceLevelEntry.migratedAt?.toString() ?? "",
                        independenceLevelEntry.migratedFrom ?? "",
                      ].join(",");
                    }),
                  ].join("\n"),
                ),
              );
            } else {
              log(
                resident.name + " (" + resident.userKey + ")の評価データは見つかりませんでした。",
              );
            }
          }

          if (exportMeasurements) {
            log(resident.name + " (" + resident.userKey + ")の測定データをダウンロードします。");

            const measurements = [];

            {
              let nextPageToken;
              do {
                let response;
                try {
                  response = await listMeasurements(
                    {
                      userKey: resident.userKey,
                      deleted: DELETION_FLAG.DEFAULT,
                      measuredAt: ["-", ...period],
                    },
                    { nextPageToken, pageSize: 1000 },
                  );
                  nextPageToken = response.nextPageToken;
                } catch (error) {
                  console.error(error);
                  log(
                    resident.name + " (" + resident.userKey + ")の評価データの取得に失敗しました。",
                  );
                  return;
                }

                if (response.measurements.length === 0) {
                  continue;
                }

                log(
                  resident.name +
                    " (" +
                    resident.userKey +
                    ")の測定データ" +
                    response.measurements.length +
                    "件をダウンロードしました。",
                );

                measurements.push(...response.measurements);
              } while (nextPageToken);
            }

            if (measurements.length > 0) {
              log(
                resident.name +
                  " (" +
                  resident.userKey +
                  ")の測定データ" +
                  measurements.length +
                  "件のダウンロードが完了しました。ファイルを作成します。",
              );

              fileContents.set(
                [
                  facilityDirectoryName,
                  residentDirectoryName,
                  resident.userKey + "_measurements.csv",
                ].join("/"),
                toBytes(
                  [
                    [
                      translate("%UserKey.0%") + " (userKey)",
                      translate("%MeasurementDateTime.0%") + " (measuredAt)",
                      translate("%MeasurementDuration.0%") + "[分] (duration)",
                      translate("%HeartRate.0%") + "[回/分] (heartRate)",
                      translate("%RespiratoryRate.0%") + "[回/分] (respiratoryRate)",
                      translate("%SensorKey.0%") + " (sensorKey)",
                      translate("%Measurer.0%") + " (measuredBy)",
                      translate("%CreationDateTime.0%") + " (createdAt)",
                      translate("%UpdateDateTime.0%") + " (updatedAt)",
                      translate("%Migration.0%") + " (migrated)",
                      translate("%MigrationDateTime.0%") + " (migratedAt)",
                      translate("%MigrationSource.0%") + " (migratedFrom)",
                    ].join(","),
                    ...measurements.map(function (measurement) {
                      return [
                        measurement.userKey ?? "",
                        measurement.measuredAt.toString(),
                        measurement.duration.total("minutes"),
                        measurement.heartRate ?? "",
                        measurement.respiratoryRate ?? "",
                        measurement.sensorKey ?? "",
                        measurement.measuredBy ?? "",
                        measurement.createdAt.toString(),
                        measurement.updatedAt.toString(),
                        measurement.migrated ?? "",
                        measurement.migratedAt?.toString() ?? "",
                        measurement.migratedFrom ?? "",
                      ].join(",");
                    }),
                  ].join("\n"),
                ),
              );
            } else {
              log(
                resident.name + " (" + resident.userKey + ")の測定データは見つかりませんでした。",
              );
            }
          }

          if (exportStatuses) {
            log(resident.name + " (" + resident.userKey + ")の状態データをダウンロードします。");

            const statuses = [];

            {
              let nextPageToken;
              do {
                let response;
                try {
                  response = await findStatuses({
                    index: "userKey-transitionedAt",
                    condition: [
                      ["userKey", "=", resident.userKey],
                      ["transitionedAt", "BETWEEN", period[0], "AND", period[1]],
                    ],
                    nextPageToken,
                  });
                  nextPageToken = response.nextPageToken;
                } catch (error) {
                  console.error(error);
                  log(
                    resident.name + " (" + resident.userKey + ")の状態データの取得に失敗しました。",
                  );
                  return new UniversalError(ERROR_CODE.ABORTED);
                }

                if (response.statuses.length === 0) {
                  continue;
                }

                log(
                  resident.name +
                    " (" +
                    resident.userKey +
                    ")の状態データ" +
                    response.statuses.length +
                    "件をダウンロードしました。",
                );

                statuses.push(...response.statuses);
              } while (nextPageToken);
            }

            if (statuses.length > 0) {
              log(
                resident.name +
                  " (" +
                  resident.userKey +
                  ")の状態データ" +
                  statuses.length +
                  "件のダウンロードが完了しました。ファイルを作成します。",
              );

              fileContents.set(
                [
                  facilityDirectoryName,
                  residentDirectoryName,
                  resident.userKey + "_statuses.csv",
                ].join("/"),
                toBytes(
                  [
                    [
                      translate("%UserKey.0%") + " (userKey)",
                      translate("%TransitionDateTime.0%") + " (transitionedAt)",
                      translate("%State.0%") + " (state)",
                      translate("%Alert.1%") + " (alerted)",
                      translate("%AlertDateTime.0%") + " (alertedAt)",
                      translate("%Confirmation.0%") + " (confirmed)",
                      translate("%ConfirmationDateTime.0%") + " (confirmedAt)",
                      translate("%Confirmer.0%") + " (confirmedBy)",
                      translate("%SensorKey.0%") + " (sensorKey)",
                      translate("%ProviderKey.0%") + " (providerKey)",
                      translate("%Transitioner.0%") + " (transitionedBy)",
                      translate("%CreationDateTime.0%") + " (createdAt)",
                      translate("%UpdateDateTime.0%") + " (updatedAt)",
                      translate("%Migration.0%") + " (migrated)",
                      translate("%MigrationDateTime.0%") + " (migratedAt)",
                      translate("%MigrationSource.0%") + " (migratedFrom)",
                    ].join(","),
                    ...statuses.map(function (status) {
                      return [
                        status.userKey ?? "",
                        status.transitionedAt,
                        status.state,
                        status.alerted ?? "",
                        status.alertedAt?.toString() ?? "",
                        status.confirmed ?? "",
                        status.confirmedAt?.toString() ?? "",
                        status.confirmedBy ?? "",
                        status.sensorKey ?? "",
                        status.providerKey ?? "",
                        status.transitionedBy ?? "",
                        status.createdAt.toString(),
                        status.updatedAt.toString(),
                        status.migrated ?? "",
                        status.migratedAt?.toString() ?? "",
                        status.migratedFrom ?? "",
                      ].join(",");
                    }),
                  ].join("\n"),
                ),
              );
            } else {
              log(
                resident.name + " (" + resident.userKey + ")の状態データは見つかりませんでした。",
              );
            }
          }

          log(resident.name + " (" + resident.userKey + ")の処理が完了しました。");
        }

        let d;
        try {
          d = await new Promise<Uint8Array<ArrayBuffer>>(function (resolve, reject) {
            zip(Object.fromEntries(fileContents), { level: 9 }, function (error, data) {
              if (error) {
                reject(error);
              } else {
                resolve(new Uint8Array(data));
              }
            });
          });
        } catch (error) {
          console.error(error);
          log("圧縮に失敗しました。");
          return new UniversalError(ERROR_CODE.ABORTED);
        }

        saveAsFile(new Blob([d], { type: "application/zip" }), facilityDirectoryName + ".zip");

        log("施設" + facilityKey + "のデータエクスポートが完了しました。");
      }),
      mergeMap(async function (parameters) {
        // エラーが伝播されたらそれを表示する。
        if (parameters instanceof Error) {
          console.error(parameters);
          await showError(parameters);
        }
      }),
    )
    .subscribe();

  return {
    logs: new Map(),
    setDependencies(dependencies) {
      set(function ({ dependencies: current }) {
        return { dependencies: { ...current, ...dependencies } };
      });
    },
    start,
    subject,
    exportFallRiskEntries: true,
    setExportFallRiskEntries(value) {
      set({ exportFallRiskEntries: value });
    },
    exportFallScoreEntries: true,
    setExportFallScoreEntries(value) {
      set({ exportFallScoreEntries: value });
    },
    exportFalls: true,
    setExportFalls(value) {
      set({ exportFalls: value });
    },
    exportIndependenceLevelEntries: true,
    setExportIndependenceLevelEntries(value) {
      set({ exportIndependenceLevelEntries: value });
    },
    exportMeasurements: true,
    setExportMeasurements(value) {
      set({ exportMeasurements: value });
    },
    exportStatuses: true,
    setExportStatuses(value) {
      set({ exportStatuses: value });
    },
  };
});
